import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import moment from 'moment'
import numeral from 'numeral'
import {
  activeDeliveryBuckets,
  DELIVERY_BUCKET_LINE_COLORS,
  DeliveryDailyPoint,
  DeliveryOverviewResponse,
  fetchDeliveryOverview,
} from './deliveryContract'

/**
 * KAN-195 — Top chart: daily delivered impressions for the current month as a
 * stacked bar (Disney MLB / Disney Non-MLB / Non-Disney), with the previous
 * month overlaid as reference (dashed lines), aligned by day-of-month.
 *
 * Self-contained chart config (combo bar + line) is set in `chartSettings` so
 * it does not depend on a single `baseChartSettings` preset.
 */
export default class DeliveryDailyChart extends SystemDashboardWidget {
  public title: string = 'Daily Delivery — Selected Month vs Previous (reference)'

  public key: string = 'delivery-daily'

  public view: string = 'chart'

  // Mixed bar/line: base chart type is overridden to `line` in chartSettings.
  public subView: string = 'bar_stacked'

  public size = {
    lg: 12,
    md: 12,
  }

  // Dashboard-wide Non-Disney toggle (broadcast from the module's filter row);
  // owning the key makes the broadcast land here and re-run the mapper.
  public filters: any = {
    show_nondisney: 'hide',
  }

  public async dataSource(): Promise<DeliveryOverviewResponse> {
    return fetchDeliveryOverview(false, this.filters.month)
  }

  /** The selected reporting month (from the dashboard month filter). */
  private get selectedMonth(): moment.Moment {
    const parsed = moment(this.filters.month, 'YYYY-MM')

    return parsed.isValid() ? parsed : moment()
  }

  protected dataMapper(res: DeliveryOverviewResponse): Promise<any> {
    const { daily_current: current, daily_previous: previous } = res

    // Cap the axis at the last delivered day of the current month (yesterday).
    // Today is excluded upstream, and the previous-month reference is trimmed to
    // the same day-of-month so it stays an apples-to-apples MTD comparison —
    // we don't paint the rest of the month.
    const lastDay = current.length
      ? Math.max(...current.map(p => moment(p.date).date()))
      : this.selectedMonth.daysInMonth()
    const categories = Array.from({ length: lastDay }, (_, i) => String(i + 1))

    // Spread a series across day-of-month slots (1-indexed day -> 0-indexed slot),
    // dropping any point past the capped last day.
    const byDay = (points: DeliveryDailyPoint[], key: keyof DeliveryDailyPoint) => {
      const out: Array<number | null> = new Array(lastDay).fill(null)
      points.forEach(p => {
        const slot = moment(p.date).date() - 1
        if (slot < lastDay) {
          out[slot] = p[key] as number
        }
      })
      return out
    }

    const buckets = activeDeliveryBuckets(this.filters.show_nondisney === 'show')

    const barColors = buckets.map(b => b.color)
    const lineColors = buckets.map(b => DELIVERY_BUCKET_LINE_COLORS[b.key])

    const series = [
      ...buckets.map(b => ({
        name: b.label,
        type: 'column',
        data: byDay(current, b.key as keyof DeliveryDailyPoint),
      })),
      ...buckets.map(b => ({
        name: `${b.label} · prev mo`,
        type: 'line',
        data: byDay(previous, b.key as keyof DeliveryDailyPoint),
      })),
    ]

    this.chartSettings = {
      chart: {
        type: 'line',
        stacked: true,
        height: 350,
        toolbar: { show: false },
        fontFamily: 'inherit',
      },
      colors: [...barColors, ...lineColors],
      plotOptions: { bar: { columnWidth: '60%', borderRadius: 2 } },
      // First N series are the bars (no stroke), next N the reference lines —
      // arrays sized to the active bucket count so the toggle stays correct.
      stroke: {
        width: [...buckets.map(() => 0), ...buckets.map(() => 2.5)],
        dashArray: [...buckets.map(() => 0), ...buckets.map(() => 5)],
        curve: 'smooth',
      },
      fill: { opacity: [...buckets.map(() => 0.95), ...buckets.map(() => 1)] },
      dataLabels: { enabled: false },
      // Legend labels are clickable to toggle each series (current & previous
      // month buckets) on/off.
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        onItemClick: { toggleDataSeries: true },
        onItemHover: { highlightDataSeries: true },
      },
      xaxis: {
        categories,
        title: { text: `${this.selectedMonth.format('MMMM YYYY')} — day of month` },
      },
      yaxis: {
        labels: { formatter: (v: number) => numeral(v).format('0.0a') },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (v: number) => (v == null ? '—' : numeral(v).format('0,0')) },
      },
      grid: { borderColor: '#eef0f3' },
      total: 0,
      labels: [],
    }

    return Promise.resolve(series)
  }
}
